import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Chess } from 'chess.js';

// Chess piece Unicode symbols
const PIECE_IMAGES = {
  'wK': '♔', 'wQ': '♕', 'wR': '♖', 'wB': '♗', 'wN': '♘', 'wP': '♙',
  'bK': '♚', 'bQ': '♛', 'bR': '♜', 'bB': '♝', 'bN': '♞', 'bP': '♟'
};

// Timer options in minutes
const TIMER_OPTIONS = [
  { label: 'No Timer', value: 0 },
  { label: '5 min', value: 5 },
  { label: '10 min', value: 10 },
  { label: '30 min', value: 30 }
];

// Celebration flowers/confetti
const FLOWERS = ['🌸', '🌺', '🌻', '🌷', '🌹', '💐', '🎉', '🎊', '✨', '⭐', '🏆', '👑'];
const LOSE_EMOJIS = ['😢', '💔', '🥺', '😔', '🤕'];

// Celebration Animation Component
const CelebrationAnimation = ({ show, isWin }) => {
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    if (show) {
      const newParticles = [];
      const count = isWin ? 50 : 20;
      const emojis = isWin ? FLOWERS : LOSE_EMOJIS;
      
      for (let i = 0; i < count; i++) {
        newParticles.push({
          id: i,
          emoji: emojis[Math.floor(Math.random() * emojis.length)],
          left: Math.random() * 100,
          delay: Math.random() * 2,
          duration: 3 + Math.random() * 2,
          size: isWin ? (20 + Math.random() * 30) : (30 + Math.random() * 20)
        });
      }
      setParticles(newParticles);
    } else {
      setParticles([]);
    }
  }, [show, isWin]);

  if (!show) return null;

  return (
    <div className="celebration-container">
      {particles.map(p => (
        <div
          key={p.id}
          className={isWin ? "flower-particle" : "lose-particle"}
          style={{
            left: `${p.left}%`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            fontSize: `${p.size}px`
          }}
        >
          {p.emoji}
        </div>
      ))}
    </div>
  );
};

// Game Result Modal
const GameResultModal = ({ show, isWin, isDraw, onNewGame, onClose }) => {
  if (!show) return null;

  return (
    <div className="result-modal-overlay">
      <div className={`result-modal ${isWin ? 'win' : isDraw ? 'draw' : 'lose'}`}>
        {isWin ? (
          <>
            <div className="result-icon">🏆</div>
            <h2 className="result-title win-title">VICTORY!</h2>
            <p className="result-message">Congratulations! You won the game!</p>
            <div className="trophy-animation">👑</div>
          </>
        ) : isDraw ? (
          <>
            <div className="result-icon">🤝</div>
            <h2 className="result-title draw-title">DRAW!</h2>
            <p className="result-message">Well played! The game ended in a draw.</p>
          </>
        ) : (
          <>
            <div className="result-icon">😔</div>
            <h2 className="result-title lose-title">GAME OVER</h2>
            <p className="result-message">Hard luck! Try again next match</p>
            <p className="motivation-text">Every master was once a beginner. Keep practicing!</p>
          </>
        )}
        <div className="result-buttons">
          <button onClick={onNewGame} className="btn-new-game">
            🎮 Play Again
          </button>
          <button onClick={onClose} className="btn-close">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const SimpleChess = () => {
  const [gameState, setGameState] = useState(() => ({
    fen: new Chess().fen(),
    turn: 'w',
    isGameOver: false,
    isCheck: false,
    isCheckmate: false,
    isDraw: false
  }));
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [validMoves, setValidMoves] = useState([]);
  const [status, setStatus] = useState('Select time and click Start');
  const [lastMove, setLastMove] = useState(null);
  const [isThinking, setIsThinking] = useState(false);
  const [difficulty, setDifficulty] = useState('medium');
  const [moveHistory, setMoveHistory] = useState([]);
  
  // Timer states
  const [timerOption, setTimerOption] = useState(5);
  const [whiteTime, setWhiteTime] = useState(5 * 60);
  const [blackTime, setBlackTime] = useState(5 * 60);
  const [gameStarted, setGameStarted] = useState(false);
  const [timerRunning, setTimerRunning] = useState(false);
  const timerRef = useRef(null);
  
  // Game result states
  const [showResult, setShowResult] = useState(false);
  const [gameResult, setGameResult] = useState({ isWin: false, isDraw: false });
  const [showCelebration, setShowCelebration] = useState(false);
  
  const gameRef = useRef(new Chess());

  // Format time as MM:SS
  const formatTime = (seconds) => {
    if (seconds <= 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Show game result with animation
  const showGameResult = (isWin, isDraw = false) => {
    setGameResult({ isWin, isDraw });
    setShowCelebration(true);
    setTimeout(() => setShowResult(true), 500);
  };

  // Timer effect
  useEffect(() => {
    if (gameStarted && timerRunning && timerOption > 0 && !gameState.isGameOver) {
      timerRef.current = setInterval(() => {
        if (gameState.turn === 'w') {
          setWhiteTime(prev => {
            if (prev <= 1) {
              clearInterval(timerRef.current);
              setStatus('Time out! AI wins!');
              setGameState(gs => ({ ...gs, isGameOver: true }));
              setTimerRunning(false);
              showGameResult(false);
              return 0;
            }
            return prev - 1;
          });
        } else {
          setBlackTime(prev => {
            if (prev <= 1) {
              clearInterval(timerRef.current);
              setStatus('Time out! You win!');
              setGameState(gs => ({ ...gs, isGameOver: true }));
              setTimerRunning(false);
              showGameResult(true);
              return 0;
            }
            return prev - 1;
          });
        }
      }, 1000);
    }
    
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [gameStarted, timerRunning, gameState.turn, gameState.isGameOver, timerOption]);

  useEffect(() => {
    gameRef.current = new Chess(gameState.fen);
  }, [gameState.fen]);

  const startGame = () => {
    const timeInSeconds = timerOption * 60;
    setWhiteTime(timeInSeconds);
    setBlackTime(timeInSeconds);
    setGameStarted(true);
    setTimerRunning(true);
    setStatus('Your turn (White)');
    setShowResult(false);
    setShowCelebration(false);
  };

  const getSquares = () => {
    const squares = [];
    const files = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
    for (let rank = 8; rank >= 1; rank--) {
      for (const file of files) {
        squares.push(file + rank);
      }
    }
    return squares;
  };

  const isLightSquare = (square) => {
    const file = square.charCodeAt(0) - 97;
    const rank = parseInt(square[1]) - 1;
    return (file + rank) % 2 === 1;
  };

  const getPieceAt = (square) => {
    const game = new Chess(gameState.fen);
    const piece = game.get(square);
    if (!piece) return null;
    return { color: piece.color, type: piece.type };
  };

  const getValidMoves = (square) => {
    const game = new Chess(gameState.fen);
    const moves = game.moves({ square, verbose: true });
    return moves.map(m => m.to);
  };

  const updateGameState = (game) => {
    setGameState({
      fen: game.fen(),
      turn: game.turn(),
      isGameOver: game.isGameOver(),
      isCheck: game.inCheck(),
      isCheckmate: game.isCheckmate(),
      isDraw: game.isDraw()
    });
  };

  const makeAIMove = useCallback((currentFen) => {
    const game = new Chess(currentFen);
    if (game.isGameOver() || game.turn() !== 'b') return;

    setIsThinking(true);
    setStatus('AI is thinking...');

    setTimeout(() => {
      const aiGame = new Chess(currentFen);
      const moves = aiGame.moves({ verbose: true });
      
      if (moves.length === 0) {
        setIsThinking(false);
        return;
      }

      let selectedMove;
      const pieceValues = { p: 1, n: 3, b: 3, r: 5, q: 9, k: 0 };

      if (difficulty === 'easy') {
        selectedMove = moves[Math.floor(Math.random() * moves.length)];
      } else if (difficulty === 'medium') {
        const captures = moves.filter(m => m.captured);
        const checks = moves.filter(m => {
          const testGame = new Chess(aiGame.fen());
          testGame.move(m);
          return testGame.inCheck();
        });
        
        if (checks.length > 0) {
          selectedMove = checks[Math.floor(Math.random() * checks.length)];
        } else if (captures.length > 0) {
          captures.sort((a, b) => (pieceValues[b.captured] || 0) - (pieceValues[a.captured] || 0));
          selectedMove = captures[0];
        } else {
          selectedMove = moves[Math.floor(Math.random() * moves.length)];
        }
      } else {
        let bestScore = -Infinity;
        for (const move of moves) {
          let score = 0;
          const testGame = new Chess(aiGame.fen());
          testGame.move(move);
          if (move.captured) score += pieceValues[move.captured] * 10;
          if (testGame.isCheckmate()) score += 1000;
          else if (testGame.inCheck()) score += 5;
          if (['d4', 'd5', 'e4', 'e5'].includes(move.to)) score += 2;
          score += Math.random() * 2;
          if (score > bestScore) {
            bestScore = score;
            selectedMove = move;
          }
        }
        if (!selectedMove) selectedMove = moves[Math.floor(Math.random() * moves.length)];
      }

      const result = aiGame.move(selectedMove);
      
      if (result) {
        setLastMove({ from: selectedMove.from, to: selectedMove.to });
        setMoveHistory(prev => [...prev, result.san]);
        updateGameState(aiGame);
        
        if (aiGame.isGameOver()) {
          setTimerRunning(false);
          if (aiGame.isCheckmate()) {
            setStatus('Checkmate! AI wins!');
            showGameResult(false);
          } else if (aiGame.isDraw()) {
            setStatus('Draw!');
            showGameResult(false, true);
          } else {
            setStatus('Game Over');
          }
        } else if (aiGame.inCheck()) {
          setStatus('Check! Your turn (White)');
        } else {
          setStatus('Your turn (White)');
        }
      }
      setIsThinking(false);
    }, 400 + Math.random() * 300);
  }, [difficulty]);

  const handleMove = useCallback((from, to) => {
    if (!gameStarted) return false;
    
    const game = new Chess(gameState.fen);
    const result = game.move({ from, to, promotion: 'q' });
    
    if (result) {
      setLastMove({ from, to });
      setMoveHistory(prev => [...prev, result.san]);
      setSelectedSquare(null);
      setValidMoves([]);
      updateGameState(game);
      
      if (game.isGameOver()) {
        setTimerRunning(false);
        if (game.isCheckmate()) {
          setStatus('Checkmate! You win!');
          showGameResult(true);
        } else if (game.isDraw()) {
          setStatus('Draw!');
          showGameResult(false, true);
        } else {
          setStatus('Game Over');
        }
      } else {
        makeAIMove(game.fen());
      }
      return true;
    }
    return false;
  }, [gameState.fen, makeAIMove, gameStarted]);

  const handleSquareClick = (square) => {
    if (!gameStarted || isThinking || gameState.isGameOver || gameState.turn !== 'w') return;
    
    const piece = getPieceAt(square);
    
    if (selectedSquare) {
      if (validMoves.includes(square)) {
        handleMove(selectedSquare, square);
        return;
      }
      if (square === selectedSquare || !piece || piece.color !== 'w') {
        setSelectedSquare(null);
        setValidMoves([]);
        return;
      }
    }
    
    if (piece && piece.color === 'w') {
      setSelectedSquare(square);
      setValidMoves(getValidMoves(square));
    } else {
      setSelectedSquare(null);
      setValidMoves([]);
    }
  };

  const handleDragStart = (e, square) => {
    if (!gameStarted || isThinking || gameState.isGameOver || gameState.turn !== 'w') {
      e.preventDefault();
      return;
    }
    const piece = getPieceAt(square);
    if (!piece || piece.color !== 'w') {
      e.preventDefault();
      return;
    }
    e.dataTransfer.setData('text/plain', square);
    setSelectedSquare(square);
    setValidMoves(getValidMoves(square));
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleDrop = (e, targetSquare) => {
    e.preventDefault();
    const sourceSquare = e.dataTransfer.getData('text/plain');
    if (!sourceSquare || sourceSquare === targetSquare) {
      setSelectedSquare(null);
      setValidMoves([]);
      return;
    }
    if (validMoves.includes(targetSquare)) {
      handleMove(sourceSquare, targetSquare);
    } else {
      setSelectedSquare(null);
      setValidMoves([]);
    }
  };

  const resetGame = () => {
    const newGame = new Chess();
    updateGameState(newGame);
    setSelectedSquare(null);
    setValidMoves([]);
    setLastMove(null);
    setMoveHistory([]);
    setIsThinking(false);
    setGameStarted(false);
    setTimerRunning(false);
    setShowResult(false);
    setShowCelebration(false);
    const timeInSeconds = timerOption * 60;
    setWhiteTime(timeInSeconds);
    setBlackTime(timeInSeconds);
    setStatus('Select time and click Start');
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const undoMove = () => {
    if (moveHistory.length < 2 || isThinking) return;
    const newGame = new Chess();
    const newHistory = moveHistory.slice(0, -2);
    for (const san of newHistory) newGame.move(san);
    updateGameState(newGame);
    setMoveHistory(newHistory);
    setSelectedSquare(null);
    setValidMoves([]);
    setLastMove(null);
    setStatus('Your turn (White)');
  };

  const renderPiece = (piece) => {
    if (!piece) return null;
    return PIECE_IMAGES[piece.color + piece.type.toUpperCase()];
  };

  const getTimerColor = (time) => {
    if (time <= 30) return '#ef4444';
    if (time <= 60) return '#f59e0b';
    return '#fff';
  };

  return (
    <div className="chess-container">
      {/* Celebration Animation */}
      <CelebrationAnimation show={showCelebration} isWin={gameResult.isWin} />
      
      {/* Game Result Modal */}
      <GameResultModal 
        show={showResult}
        isWin={gameResult.isWin}
        isDraw={gameResult.isDraw}
        onNewGame={resetGame}
        onClose={() => setShowResult(false)}
      />

      <h1 className="chess-title">Chess vs AI</h1>
      
      {/* Timer Selection */}
      {!gameStarted && (
        <div className="setup-panel">
          <div className="setup-label">Select Time Control</div>
          <div className="timer-options">
            {TIMER_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => {
                  setTimerOption(opt.value);
                  setWhiteTime(opt.value * 60);
                  setBlackTime(opt.value * 60);
                }}
                className={`timer-btn ${timerOption === opt.value ? 'active' : ''}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          
          <div className="difficulty-options">
            {['easy', 'medium', 'hard'].map(level => (
              <button
                key={level}
                onClick={() => setDifficulty(level)}
                className={`difficulty-btn ${difficulty === level ? 'active' : ''}`}
              >
                {level}
              </button>
            ))}
          </div>
          
          <button onClick={startGame} className="start-btn" data-testid="start-game-btn">
            ▶ START GAME
          </button>
        </div>
      )}
      
      {/* Timers Display */}
      {gameStarted && timerOption > 0 && (
        <div className="timers-container">
          <div className={`timer-box black ${gameState.turn === 'b' ? 'active' : ''}`}>
            <div className="timer-label">AI (Black)</div>
            <div className="timer-value" style={{ color: getTimerColor(blackTime) }}>
              {formatTime(blackTime)}
            </div>
          </div>
          
          <div className={`timer-box white ${gameState.turn === 'w' ? 'active' : ''}`}>
            <div className="timer-label">You (White)</div>
            <div className="timer-value" style={{ color: getTimerColor(whiteTime) }}>
              {formatTime(whiteTime)}
            </div>
          </div>
        </div>
      )}
      
      {/* Chess Board */}
      <div className="board-container">
        <div className="file-labels top">
          {['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'].map(f => (
            <span key={f}>{f}</span>
          ))}
        </div>
        
        <div className="board-with-ranks">
          <div className="rank-labels">
            {[8, 7, 6, 5, 4, 3, 2, 1].map(r => (
              <span key={r}>{r}</span>
            ))}
          </div>
          
          <div className="chess-board">
            {getSquares().map((square) => {
              const piece = getPieceAt(square);
              const isSelected = selectedSquare === square;
              const isValidMove = validMoves.includes(square);
              const isLastMoveSquare = lastMove && (lastMove.from === square || lastMove.to === square);
              const light = isLightSquare(square);
              
              let bgColor = light ? '#f0d9b5' : '#b58863';
              if (isSelected) bgColor = '#7fc97f';
              else if (isLastMoveSquare) bgColor = light ? '#f7f769' : '#baca44';
              
              return (
                <div
                  key={square}
                  onClick={() => handleSquareClick(square)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, square)}
                  className="square"
                  style={{ backgroundColor: bgColor }}
                  data-testid={`square-${square}`}
                >
                  {isValidMove && (
                    <div className={`valid-move-indicator ${piece ? 'capture' : ''}`} />
                  )}
                  {piece && (
                    <div
                      draggable={gameStarted && piece.color === 'w' && gameState.turn === 'w' && !isThinking}
                      onDragStart={(e) => handleDragStart(e, square)}
                      className={`piece ${piece.color === 'w' ? 'white' : 'black'}`}
                    >
                      {renderPiece(piece)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Status */}
      <div className={`status-box ${isThinking ? 'thinking' : ''} ${gameState.isCheck ? 'check' : ''}`}>
        <p>{status} {isThinking && '⏳'}</p>
      </div>
      
      {/* Controls */}
      {gameStarted && (
        <div className="controls">
          <button onClick={resetGame} disabled={isThinking} className="control-btn new-game">
            New Game
          </button>
          <button
            onClick={undoMove}
            disabled={moveHistory.length < 2 || isThinking}
            className="control-btn undo"
          >
            Undo
          </button>
        </div>
      )}
      
      {/* Move History */}
      {moveHistory.length > 0 && (
        <div className="move-history">
          <div className="history-title">Move History</div>
          <div className="moves">
            {moveHistory.map((move, i) => (
              <span key={i} className={`move ${i % 2 === 0 ? 'white-move' : 'black-move'}`}>
                {i % 2 === 0 && <span className="move-number">{Math.floor(i/2)+1}.</span>} {move}
              </span>
            ))}
          </div>
        </div>
      )}

      <style>{`
        .chess-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          min-height: 100vh;
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
          padding: 20px;
          font-family: system-ui, sans-serif;
          position: relative;
          overflow: hidden;
        }

        .chess-title {
          color: #fff;
          margin-bottom: 15px;
          font-size: 28px;
          font-weight: bold;
        }

        /* Celebration Animation Styles */
        .celebration-container {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: 100;
          overflow: hidden;
        }

        .flower-particle {
          position: absolute;
          top: -50px;
          animation: flowerFall linear forwards;
        }

        .lose-particle {
          position: absolute;
          top: 50%;
          left: 50%;
          animation: losePulse 2s ease-out forwards;
          opacity: 0;
        }

        @keyframes flowerFall {
          0% {
            transform: translateY(0) rotate(0deg) scale(1);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg) scale(0.5);
            opacity: 0;
          }
        }

        @keyframes losePulse {
          0% {
            transform: translate(-50%, -50%) scale(0);
            opacity: 0;
          }
          50% {
            transform: translate(-50%, -50%) scale(2);
            opacity: 1;
          }
          100% {
            transform: translate(-50%, -50%) scale(3);
            opacity: 0;
          }
        }

        /* Result Modal Styles */
        .result-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 200;
          animation: fadeIn 0.3s ease-out;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .result-modal {
          background: linear-gradient(135deg, #1e1e2f 0%, #2a2a4a 100%);
          border-radius: 20px;
          padding: 40px;
          text-align: center;
          max-width: 400px;
          width: 90%;
          animation: modalSlideIn 0.5s ease-out;
          border: 2px solid;
        }

        .result-modal.win {
          border-color: #ffd700;
          box-shadow: 0 0 50px rgba(255, 215, 0, 0.3);
        }

        .result-modal.lose {
          border-color: #ff6b6b;
          box-shadow: 0 0 30px rgba(255, 107, 107, 0.2);
        }

        .result-modal.draw {
          border-color: #74b9ff;
          box-shadow: 0 0 30px rgba(116, 185, 255, 0.2);
        }

        @keyframes modalSlideIn {
          from {
            transform: translateY(-50px) scale(0.8);
            opacity: 0;
          }
          to {
            transform: translateY(0) scale(1);
            opacity: 1;
          }
        }

        .result-icon {
          font-size: 80px;
          margin-bottom: 20px;
          animation: iconBounce 1s ease infinite;
        }

        @keyframes iconBounce {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }

        .result-title {
          font-size: 36px;
          font-weight: bold;
          margin-bottom: 15px;
        }

        .win-title {
          color: #ffd700;
          text-shadow: 0 0 20px rgba(255, 215, 0, 0.5);
        }

        .lose-title {
          color: #ff6b6b;
        }

        .draw-title {
          color: #74b9ff;
        }

        .result-message {
          color: #ddd;
          font-size: 18px;
          margin-bottom: 10px;
        }

        .motivation-text {
          color: #888;
          font-size: 14px;
          font-style: italic;
          margin-bottom: 20px;
        }

        .trophy-animation {
          font-size: 40px;
          animation: trophySpin 2s linear infinite;
          margin-bottom: 20px;
        }

        @keyframes trophySpin {
          0% { transform: rotateY(0deg); }
          100% { transform: rotateY(360deg); }
        }

        .result-buttons {
          display: flex;
          gap: 15px;
          justify-content: center;
          margin-top: 20px;
        }

        .btn-new-game {
          padding: 12px 30px;
          font-size: 16px;
          font-weight: bold;
          background: linear-gradient(135deg, #6c5ce7, #a29bfe);
          color: white;
          border: none;
          border-radius: 10px;
          cursor: pointer;
          transition: transform 0.2s, box-shadow 0.2s;
        }

        .btn-new-game:hover {
          transform: scale(1.05);
          box-shadow: 0 5px 20px rgba(108, 92, 231, 0.4);
        }

        .btn-close {
          padding: 12px 30px;
          font-size: 16px;
          background: rgba(255, 255, 255, 0.1);
          color: #aaa;
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-close:hover {
          background: rgba(255, 255, 255, 0.2);
          color: white;
        }

        /* Setup Panel */
        .setup-panel {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 15px;
          margin-bottom: 20px;
          padding: 20px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 12px;
        }

        .setup-label {
          color: #aaa;
          font-size: 14px;
        }

        .timer-options, .difficulty-options {
          display: flex;
          gap: 10px;
        }

        .timer-btn, .difficulty-btn {
          padding: 10px 18px;
          font-size: 14px;
          background: rgba(255, 255, 255, 0.1);
          color: #fff;
          border: 2px solid transparent;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
          text-transform: capitalize;
        }

        .timer-btn.active {
          background: #6c5ce7;
          border-color: #a29bfe;
        }

        .difficulty-btn.active {
          background: #e17055;
        }

        .start-btn {
          padding: 14px 50px;
          font-size: 18px;
          font-weight: bold;
          background: #22c55e;
          color: #fff;
          border: none;
          border-radius: 10px;
          cursor: pointer;
          margin-top: 10px;
          box-shadow: 0 4px 15px rgba(34, 197, 94, 0.4);
          transition: all 0.2s;
        }

        .start-btn:hover {
          transform: scale(1.05);
          box-shadow: 0 6px 20px rgba(34, 197, 94, 0.5);
        }

        /* Timers */
        .timers-container {
          display: flex;
          justify-content: space-between;
          width: 100%;
          max-width: 400px;
          margin-bottom: 15px;
        }

        .timer-box {
          padding: 12px 25px;
          border-radius: 10px;
          transition: all 0.3s;
        }

        .timer-box.black {
          background: rgba(0, 0, 0, 0.4);
          border: 2px solid transparent;
        }

        .timer-box.black.active {
          background: rgba(0, 0, 0, 0.8);
          border-color: #ef4444;
        }

        .timer-box.white {
          background: rgba(255, 255, 255, 0.05);
          border: 2px solid transparent;
        }

        .timer-box.white.active {
          background: rgba(255, 255, 255, 0.15);
          border-color: #22c55e;
        }

        .timer-label {
          color: #888;
          font-size: 11px;
          margin-bottom: 2px;
        }

        .timer-value {
          font-size: 28px;
          font-weight: bold;
          font-family: monospace;
        }

        /* Board */
        .board-container {
          padding: 12px;
          background: #8b4513;
          border-radius: 8px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
        }

        .file-labels {
          display: flex;
          justify-content: space-around;
          padding-left: 18px;
        }

        .file-labels span {
          color: #fff;
          font-size: 11px;
          width: 42px;
          text-align: center;
        }

        .file-labels.top {
          margin-bottom: 3px;
        }

        .board-with-ranks {
          display: flex;
        }

        .rank-labels {
          display: flex;
          flex-direction: column;
          justify-content: space-around;
          margin-right: 3px;
        }

        .rank-labels span {
          color: #fff;
          font-size: 11px;
          height: 42px;
          line-height: 42px;
        }

        .chess-board {
          display: grid;
          grid-template-columns: repeat(8, 42px);
          grid-template-rows: repeat(8, 42px);
          border: 2px solid #5d3a1a;
        }

        .square {
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          cursor: pointer;
        }

        .valid-move-indicator {
          position: absolute;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: rgba(0, 0, 0, 0.2);
          pointer-events: none;
        }

        .valid-move-indicator.capture {
          width: 100%;
          height: 100%;
          border-radius: 0;
          background: rgba(255, 0, 0, 0.4);
          border: 2px solid rgba(255, 0, 0, 0.6);
          box-sizing: border-box;
        }

        .piece {
          font-size: 34px;
          cursor: grab;
          z-index: 2;
          transition: transform 0.1s;
        }

        .piece.white {
          color: #fff;
          text-shadow: 1px 1px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000;
        }

        .piece.black {
          color: #000;
          text-shadow: 1px 1px 0 #666;
        }

        /* Status */
        .status-box {
          margin-top: 15px;
          padding: 10px 20px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 8px;
          border: 2px solid transparent;
        }

        .status-box.thinking {
          background: rgba(108, 92, 231, 0.3);
        }

        .status-box.check {
          border-color: #e74c3c;
        }

        .status-box p {
          color: #fff;
          font-size: 16px;
          font-weight: 600;
          margin: 0;
        }

        /* Controls */
        .controls {
          display: flex;
          gap: 12px;
          margin-top: 15px;
        }

        .control-btn {
          padding: 10px 25px;
          font-size: 14px;
          font-weight: 600;
          color: #fff;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .control-btn.new-game {
          background: #6c5ce7;
        }

        .control-btn.undo {
          background: #e17055;
        }

        .control-btn:disabled {
          background: #555;
          cursor: not-allowed;
        }

        /* Move History */
        .move-history {
          margin-top: 15px;
          padding: 12px 15px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 8px;
          max-width: 360px;
          width: 100%;
        }

        .history-title {
          color: #888;
          font-size: 12px;
          margin-bottom: 8px;
        }

        .moves {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
          font-size: 13px;
        }

        .move {
          padding: 3px 6px;
          border-radius: 4px;
          color: #fff;
        }

        .white-move {
          background: rgba(108, 92, 231, 0.3);
        }

        .black-move {
          background: rgba(255, 255, 255, 0.1);
        }

        .move-number {
          color: #888;
        }
      `}</style>
    </div>
  );
};

export default SimpleChess;
